import React, { FC, useEffect, useMemo, useState } from 'react';
import { DatePicker, InputNumber, Modal, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { assetsRestCrudThunks } from '../../thunks/assets_thunks';
import { getCurrency } from '../../utils/DataAccessUtils';

// Matches AssetForm.tsx — keep both in sync if the format changes.
const DATE_FORMAT = 'MMMM DD, YYYY';

type MissingInputKey = 'purchase_price' | 'install_date' | 'life_expectancy';
type FieldInputType = 'currency' | 'months' | 'date';

interface FieldSpec {
	title: string;
	label: string;
	inputType: FieldInputType;
	patchField: string;
}

const FIELD_SPECS: Record<MissingInputKey, FieldSpec> = {
	purchase_price: {
		title: 'Set Purchase Price',
		label: 'Purchase Price',
		inputType: 'currency',
		patchField: 'originalPrice',
	},
	install_date: {
		title: 'Set Install Date',
		label: 'Install Date',
		inputType: 'date',
		patchField: 'installDate',
	},
	life_expectancy: {
		title: 'Set Estimated Useful Life',
		label: 'Estimated Useful Life',
		inputType: 'months',
		patchField: 'lifeExpectancyInMonths',
	},
};

const isSupportedKey = (key: string | null | undefined): key is MissingInputKey =>
	key === 'purchase_price' || key === 'install_date' || key === 'life_expectancy';

interface ImproveConfidenceModalProps {
	fieldKey: string | null;
	assetId: string | number;
	userType: string;
	onClose: () => void;
	onSaved: () => void;
}

export const ImproveConfidenceModal: FC<ImproveConfidenceModalProps> = ({
	fieldKey,
	assetId,
	userType,
	onClose,
	onSaved,
}) => {
	const dispatch = useDispatch();
	const currentUser = useSelector((state: any) => state.session.currentUser);
	const currency = useMemo(() => getCurrency({ currentUser }), [currentUser]);

	const [numberValue, setNumberValue] = useState<number | null>(null);
	const [dateValue, setDateValue] = useState<moment.Moment | null>(null);
	const [saving, setSaving] = useState(false);

	const spec = useMemo(() => (isSupportedKey(fieldKey) ? FIELD_SPECS[fieldKey] : null), [fieldKey]);
	const open = !!spec;

	useEffect(() => {
		if (open) {
			setNumberValue(null);
			setDateValue(null);
			setSaving(false);
		}
	}, [open, fieldKey]);

	const handleOk = () => {
		if (!spec) return;
		const value =
			spec.inputType === 'date' ? (dateValue ? dateValue.toISOString() : null) : numberValue;
		if (value === null || value === undefined) {
			message.warning(`Please enter a ${spec.label.toLowerCase()}.`);
			return;
		}
		setSaving(true);
		(dispatch as any)(
			assetsRestCrudThunks(userType).updatePartially(assetId, { [spec.patchField]: value })
		)
			.then(() => {
				setSaving(false);
				message.success(`${spec.label} updated.`);
				onSaved();
				onClose();
			})
			.catch((err: any) => {
				setSaving(false);
				message.error(
					typeof err === 'string' ? err : `Failed to update ${spec.label.toLowerCase()}.`
				);
			});
	};

	const renderInput = () => {
		if (!spec) return null;
		if (spec.inputType === 'date') {
			return (
				<DatePicker
					value={dateValue}
					onChange={(value) => setDateValue(value)}
					format={DATE_FORMAT}
					style={{ width: '100%' }}
					autoFocus
				/>
			);
		}
		const addon = spec.inputType === 'currency' ? currency.id : 'months';
		return (
			<InputNumber
				min={0}
				value={numberValue as any}
				onChange={(value) => setNumberValue((value as number) || null)}
				addonAfter={addon}
				style={{ width: '100%' }}
				autoFocus
			/>
		);
	};

	return (
		<Modal
			title={spec ? spec.title : ''}
			visible={open}
			onCancel={onClose}
			onOk={handleOk}
			okButtonProps={{ loading: saving }}
			destroyOnClose
		>
			{spec ? (
				<div className="flex flex-col gap-xs">
					<label className="bodymedium text-character-primary">{spec.label}</label>
					{renderInput()}
				</div>
			) : null}
		</Modal>
	);
};

export default ImproveConfidenceModal;
